/**
 * Tech stack tiers + template ordering, shared by every page that draws the
 * pyramid or lists the template (TechStack, StartTechStack, CRMtechstack,
 * TechStackTemplate, AllSoftware, SoftwareOverview).
 *
 * The three tiers are fixed — their ids (core / primary / secondary, doc ids
 * Core / Primary / Secondary) key every client selection and never change — but
 * their display name and pyramid position are data on technologyStack/{DocId}:
 *   { title?: string, order?: number }
 * Tool types and software options may carry `order` too (set by the MCP reorder
 * tool). Anything without an order falls back to the old sort.
 * Mirrored server-side in functions/mcp/techStack.js.
 */
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

export const TECH_TIERS = [
  { id: 'core', docId: 'Core', defaultTitle: 'Core' },
  { id: 'primary', docId: 'Primary', defaultTitle: 'Primary' },
  { id: 'secondary', docId: 'Secondary', defaultTitle: 'Secondary' },
];

export const DEFAULT_TIER_ORDER = TECH_TIERS.map((t) => t.id);

/** Pyramid styling goes by position (top layer narrowest), not by tier. */
export const PYRAMID_LAYERS = [
  { color: 'bg-red-700', width: '40%' },
  { color: 'bg-red-600', width: '70%' },
  { color: 'bg-red-500', width: '100%' },
];

const hasOrder = (item) => Number.isFinite(item?.order);

/** Ordered items first (by order), then the rest by `fallback`. */
export const byTemplateOrder = (fallback) => (a, b) => {
  if (hasOrder(a) && hasOrder(b)) return a.order - b.order || fallback(a, b);
  if (hasOrder(a)) return -1;
  if (hasOrder(b)) return 1;
  return fallback(a, b);
};

const byName = (a, b) => (a?.name || '').localeCompare(b?.name || '');

/** Tool types: explicit order, else alphabetical (as the CRM editor always did). */
export const compareTechTypes = byTemplateOrder(byName);

/** Software options: explicit order, else suggested first, then alphabetical. */
export const compareTechSoftware = byTemplateOrder((a, b) => {
  if (!!a?.suggested !== !!b?.suggested) return a?.suggested ? -1 : 1;
  return byName(a, b);
});

/** Tier names + order from technologyStack/{Core|Primary|Secondary}, top of the pyramid first. */
export async function fetchTechTierMeta() {
  let docs = {};
  try {
    const snap = await getDocs(collection(db, 'technologyStack'));
    docs = Object.fromEntries(snap.docs.map((d) => [d.id, d.data() || {}]));
  } catch (error) {
    console.error('Error loading tech stack tier settings:', error);
  }
  return TECH_TIERS.map((t, i) => {
    const data = docs[t.docId] || {};
    return {
      ...t,
      title: String(data.title || '').trim() || t.defaultTitle,
      order: Number.isFinite(data.order) ? data.order : i,
    };
  }).sort((a, b) => a.order - b.order);
}

/** Copy titles + positional colour/width onto a { core, primary, secondary } pyramid state. */
export function applyTierMeta(stack, meta) {
  const next = { ...stack };
  meta.forEach((t, i) => {
    if (next[t.id]) next[t.id] = { ...next[t.id], title: t.title, ...PYRAMID_LAYERS[i] };
  });
  return next;
}

/** { Core: 'Foundation', ... } — the display name for a tier doc id. */
export const tierTitlesByDocId = (meta) => Object.fromEntries(meta.map((t) => [t.docId, t.title]));
